import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Landmark, X } from "lucide-react";
import { Suspense } from "react";
import { twMerge } from "tailwind-merge";
import { env } from "@/../env";
import givefrontIcon from "@/assets/brand/givefrontlogofull.png";
import { TableSkeleton } from "@/components/skeletons/table-skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useProdMode } from "@/hooks/use-prod-mode";
import { PeriodSpendCard } from "@/routes/_authed/_org/dashboard/-components/stats/period-spend-card";
import {
  StatCard,
  StatCardSuspense,
} from "@/routes/_authed/_org/dashboard/-components/stats/stat-card";
import { TransparencyTransactionsView } from "./-components/transactions-view";
import { TransparencyUsersView } from "./-components/users-view";

export const Route = createFileRoute("/transparency/$orgId")({
  beforeLoad: async ({ context: { queryClient, trpc }, params: { orgId } }) => {
    await queryClient.ensureQueryData(
      trpc.public.orgDetails.queryOptions({ orgId }),
    );
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const { orgId } = Route.useParams();

  const { data: orgDetails } = useSuspenseQuery(
    trpc.public.orgDetails.queryOptions({
      orgId,
    }),
  );

  const prodMode = useProdMode();

  return (
    <div className="flex min-h-screen flex-col items-center pb-24">
      <div className="flex h-9 w-full items-center justify-center gap-2 bg-green-600 text-sm text-white">
        <Landmark className="size-4" />
        {orgDetails!.dbaName || orgDetails!.name} has made their finances
        public.{" "}
        <a
          href="https://givefront.com?utm=transparency"
          target="_blank"
          className="underline"
          rel="noopener"
        >
          What's Givefront?
        </a>
      </div>

      <main className="flex w-full max-w-5xl flex-col px-8 pt-16 text-start">
        <h1 className="flex items-center gap-3 font-semibold text-4xl">
          <img
            src={givefrontIcon}
            className={twMerge(
              env.VITE_ENVIRONMENT !== "production" && !prodMode && "grayscale",
              "h-12 rounded-md opacity-50",
            )}
            alt=""
          />
          <X className="size-4 text-neutral-600" />
          {orgDetails!.dbaName || orgDetails!.name}
        </h1>

        <div className="mt-4 h-px w-full bg-border" />

        <div className="@container/col mt-8 flex h-48 gap-4 px-2">
          <StatCardSuspense>
            <PeriodSpendCard orgId={orgId} />
          </StatCardSuspense>

          <div className="grid h-full w-full shrink-0 @4xl/col:basis-1/2 @2xl/col:grid-cols-4 @4xl/col:grid-cols-2 grid-cols-2 @2xl/col:grid-rows-1 @4xl/col:grid-rows-2 grid-rows-2 gap-4">
            <StatCardSuspense>
              <StatCard title="Active cards" icon="credit-card" gridItem>
                <span className="font-bold text-xl">
                  {orgDetails.activeCardsCount}
                </span>
              </StatCard>
            </StatCardSuspense>
            <StatCardSuspense>
              <StatCard title="Staff on team" icon="users" gridItem>
                <span className="font-bold text-xl">
                  {orgDetails.staffCount}
                </span>
              </StatCard>
            </StatCardSuspense>
            <StatCardSuspense>
              <StatCard title="Pending requests" icon="timer" gridItem>
                <span className="font-bold text-xl">
                  {orgDetails.pendingRequestsCount}
                </span>
              </StatCard>
            </StatCardSuspense>
            <StatCardSuspense>
              <StatCard
                title="Active budgets"
                icon="circle-dollar-sign"
                gridItem
              >
                <span className="font-bold text-xl">
                  {orgDetails.activeBudgetsCount}
                </span>
              </StatCard>
            </StatCardSuspense>
          </div>
        </div>

        <Tabs defaultValue="transactions">
          <TabsList className="mt-16 mb-8 w-full gap-3 border-border border-b pb-3 pl-4">
            <TabsTrigger
              value="transactions"
              count={orgDetails.transactionCount}
            >
              Transactions
            </TabsTrigger>
            <TabsTrigger value="people" count={orgDetails.staffCount}>
              People
            </TabsTrigger>
          </TabsList>
          <TabsContent value="transactions">
            <TransparencyTransactionsView orgId={orgId} />
          </TabsContent>
          <TabsContent value="people">
            <Suspense fallback={<TableSkeleton />}>
              <TransparencyUsersView orgId={orgId} />
            </Suspense>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
